"use client";

import CloseIcon from "@mui/icons-material/Close";
import { Button, IconButton } from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import NextLink from "next/link";
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from "react";

/** An optional link shown as a button beside the message. */
export interface SnackbarLink {
  label: string;
  href: string;
}

interface SnackbarContextProps {
  showSnackbarMessage: (
    message: string,
    type: SnackbarType,
    link?: SnackbarLink,
  ) => void;
}

type SnackbarType = "success" | "error";

const SnackbarContext = createContext<SnackbarContextProps | undefined>(
  undefined,
);

/**
 * A context provider so that we can show snackbar messages from anywhere in the app using
 * the showSnackbarMessage function.
 */
export const SnackbarProvider = ({ children }: { children: ReactNode }) => {
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarType, setSnackbarType] = useState<SnackbarType>("success");
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [snackbarLink, setSnackbarLink] = useState<SnackbarLink | null>(null);

  const showSnackbarMessage = useCallback(
    (message: string, type: SnackbarType, link?: SnackbarLink) => {
      setSnackbarType(type);
      setSnackbarMessage(message);
      setSnackbarLink(link ?? null);
      setSnackbarOpen(true);
    },
    [],
  );

  return (
    <SnackbarContext.Provider value={{ showSnackbarMessage }}>
      {children}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        message={snackbarMessage}
        action={
          <>
            {snackbarLink && (
              <Button
                component={NextLink}
                href={snackbarLink.href}
                size="small"
                color="inherit"
                onClick={() => setSnackbarOpen(false)}
              >
                {snackbarLink.label}
              </Button>
            )}
            <IconButton
              size="small"
              aria-label="Close"
              color="inherit"
              onClick={() => setSnackbarOpen(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
        ContentProps={{
          sx: {
            backgroundColor: snackbarType === "success" ? "green" : "red",
          },
        }}
      />
    </SnackbarContext.Provider>
  );
};

export const useCustomSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useCustomSnackbar must be used within a SnackbarProvider");
  }
  return context;
};
